import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllERC20TokenBalance } from '../utils/tokenBalance';
import { TokenBalanceItem } from '../types/tokenBalance';
import { selectTokens } from './tokenListSlice';

// 定义token的类型
interface Token {
  address: string;
  decimals: string | null;
  name: string | null;
  symbol: string | null;
  type: string;
  icon_url: string | null;
  source_platform: string;
}

// 定义用户token的类型
export interface UserToken {
  token: Token;
  token_id: string | null;
  token_instance: Record<string, unknown> | null;
  value: string;
}

// 将OKX API数据转换为现有的UserToken结构
const transformOKXDataToUserToken = (okxData: TokenBalanceItem, tokenList: Token[]): UserToken => {
  // 在tokenList中查找匹配的代币（只通过地址匹配）
  const matchedToken = tokenList.find(token => 
    token.address.toLowerCase() === okxData.tokenContractAddress.toLowerCase()
  );

  // 如果找到匹配的代币，使用其信息，否则使用默认值
  const token = matchedToken || {
    address: okxData.tokenContractAddress,
    decimals:  '18', // OKX API没有提供decimals，默认使用18
    name: okxData.symbol,
    symbol: okxData.symbol,
    type: okxData.tokenType === 'ERC20' ? 'ERC-20' : okxData.tokenType,
    icon_url: null, // 需要根据symbol设置默认图标
    source_platform: 'okx_api'
  };

  return {
    token,
    token_id: okxData.tokenId || null,
    token_instance: null,
    value: okxData.holdingAmount
  };
};

// 设置代币图标
const getTokenIcon = (symbol: string): string => {
  const symbolLower = symbol.toLowerCase();
  const iconMap: Record<string, string> = {
    'usdt': '/images/usdt.svg',
    'usdc.e': '/images/usdc.e.svg',
    'weth': '/images/weth.svg',
    'okb': '/images/okb.png',
    'wokb': '/images/okb.png'
  };
  
  return iconMap[symbolLower] || '/images/default.png';
};

// State类型
interface UserTokensState {
  items: UserToken[];
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

// 初始state
const initialState: UserTokensState = {
  items: [],
  loading: false,
  error: null,
  lastUpdated: null
};

// 异步获取用户token列表
export const fetchUserTokens = createAsyncThunk(
  'userTokens/fetch',
  async (address: string, { getState }) => {
    try {
      // 使用新的OKX API获取ERC-20代币余额
      const okxTokens = await getAllERC20TokenBalance(address, 'XLAYER');
      
      // 获取tokenList中的所有代币
      const state = getState() as any;
      const tokenList = selectTokens(state);
      
      // 转换OKX数据为UserToken格式
      const processedItems = okxTokens.map((okxToken) => {
        const userToken = transformOKXDataToUserToken(okxToken, tokenList);
        
        // 如果token中没有icon_url，则使用默认图标
        if (!userToken.token.icon_url) {
          userToken.token.icon_url = getTokenIcon(okxToken.symbol);
        }
        
        return userToken;
      });


      // 将OKB放在列表第一位，然后是按价值排序的其他代币
      const sortedTokens = processedItems.sort((a, b) => {
        const aValue = parseFloat(a.value) * parseFloat(okxTokens.find(t => t.tokenContractAddress === a.token.address)?.priceUsd || '0');
        const bValue = parseFloat(b.value) * parseFloat(okxTokens.find(t => t.tokenContractAddress === b.token.address)?.priceUsd || '0');
        return bValue - aValue; // 按价值降序排列
      });

      return [ ...sortedTokens];
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }
);

// 添加手动刷新action
export const refreshUserTokens = createAsyncThunk(
  'userTokens/refresh',
  async (address: string, { getState }) => {
    try {
      // 使用新的OKX API获取ERC-20代币余额
      const okxTokens = await getAllERC20TokenBalance(address, 'XLAYER');
      
      // 获取tokenList中的所有代币
      const state = getState() as any;
      const tokenList = selectTokens(state);
      
      // 转换OKX数据为UserToken格式
      const processedItems = okxTokens.map((okxToken) => {
        const userToken = transformOKXDataToUserToken(okxToken, tokenList);
        
        // 如果token中没有icon_url，则使用默认图标
        if (!userToken.token.icon_url) {
          userToken.token.icon_url = getTokenIcon(okxToken.symbol);
        }
        
        return userToken;
      });

      // 获取原生OKB余额
      // const nativeOKB = await getNativeOKBBalance(address);

      // // 构造OKB token数据（原生代币）
      // const OKBToken = {
      //   token: {
      //     address: "0x0000000000000000000000000000000000000000",
      //     decimals: "18",
      //     name: "OKB",
      //     symbol: "OKB",
      //     type: "NATIVE",
      //     icon_url: "/img/okb.png",
      //     source_platform: "native"
      //   },
      //   token_id: null,
      //   token_instance: null,
      //   value: nativeOKB
      // };

      // 将OKB放在列表第一位，然后是按价值排序的其他代币
      const sortedTokens = processedItems.sort((a, b) => {
        const aValue = parseFloat(a.value) * parseFloat(okxTokens.find(t => t.tokenContractAddress === a.token.address)?.priceUsd || '0');
        const bValue = parseFloat(b.value) * parseFloat(okxTokens.find(t => t.tokenContractAddress === b.token.address)?.priceUsd || '0');
        return bValue - aValue; // 按价值降序排列
      });

      return [ ...sortedTokens];
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }
);

// Slice
const userTokensSlice = createSlice({
  name: 'userTokens',
  initialState,
  reducers: {
    clearUserTokens: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserTokens.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '未知错误';
      })
      .addCase(refreshUserTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshUserTokens.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
        state.lastUpdated = Date.now();
      })
      .addCase(refreshUserTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '未知错误';
      });
  },
});

// 选择器
export const selectUserTokens = (state: any) => {
  return state.userTokens?.items || [];
};

export const selectUserTokensLoading = (state: any) => {
  return state.userTokens?.loading || false;
};

export const selectUserTokensError = (state: any) => {
  return state.userTokens?.error || null;
};

export const selectUserTokensLastUpdated = (state: any) => {
  return state.userTokens?.lastUpdated || null;
};

// 派生选择器
export const selectUserTokensCount = (state: any) => {
  return selectUserTokens(state).length;
};

export const selectUserTokensBySymbol = (state: any, symbol: string) => {
  return selectUserTokens(state).find(
    (userToken: UserToken) => userToken.token.symbol?.toLowerCase() === symbol.toLowerCase()
  );
};

export const selectUserTokensByAddress = (state: any, address: string) => {
  return selectUserTokens(state).find(
    (userToken: UserToken) => userToken.token.address.toLowerCase() === address.toLowerCase()
  );
};

export const selectUserTokensWithValue = (state: any) => {
  return selectUserTokens(state).filter(
    (userToken: UserToken) => parseFloat(userToken.value) > 0
  );
};

export const selectUserTokensTotalValue = (state: any) => {
  return selectUserTokens(state).reduce((total: number, userToken: UserToken) => {
    return total + parseFloat(userToken.value || '0');
  }, 0);
};

// 导出actions
export const { clearUserTokens } = userTokensSlice.actions;

export default userTokensSlice.reducer; 